import { Link } from "react-router-dom";
import type { ContentItem } from "@/mocks/content";
import { detailPath, formatDate, typeMeta } from "@/lib/content";

interface ContentCardProps {
  item: ContentItem;
  variant?: "vertical" | "horizontal";
}

export default function ContentCard({ item, variant = "vertical" }: ContentCardProps) {
  const meta = typeMeta[item.type];

  if (variant === "horizontal") {
    return (
      <Link
        to={detailPath(item)}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
      >
        <div className="h-24 w-36 shrink-0 overflow-hidden rounded-md bg-background-100 md:h-28 md:w-44">
          <img
            src={item.cover}
            alt={`${item.title} 封面图`}
            title={`${item.title} - 家庭教师动画平台`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
              {meta.short}
            </span>
            {item.series && (
              <span className="text-xs text-foreground-400">{item.series}</span>
            )}
          </div>
          <h3 className="line-clamp-2 font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
            {item.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-sm">
            {item.summary}
          </p>
          <div className="mt-auto flex items-center gap-3 pt-3 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true" />
              {formatDate(item.date)}
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <i className="ri-time-line" aria-hidden="true" />
              {item.readTime}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={detailPath(item)}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-44 w-full overflow-hidden bg-background-100">
        <img
          src={item.cover}
          alt={`${item.title} 封面图`}
          title={`${item.title} - 家庭教师动画平台`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
            {meta.short}
          </span>
          {item.year && <span className="text-xs text-foreground-400">{item.year}</span>}
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-500">
          {item.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" aria-hidden="true" />
            {formatDate(item.date)}
          </span>
          <span className="flex items-center gap-1 transition-colors group-hover:text-accent-600">
            阅读全文
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}